import { Component, ChangeDetectionStrategy, OnInit, OnDestroy, ViewChild } from '@angular/core';
import {
  AbstractControl,
  AbstractControlOptions,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { debounceTime, filter, map, share, skipWhile, startWith, switchMap, take } from 'rxjs/operators';

import { LocationRouteCffService } from '@fcom/core';
import { Location, LocationSuggestions } from '@fcom/core-api';
import { locationFormatValidator } from '@fcom/service-forms/services/form-validators';
import { ReactiveFormStatus } from '@fcom/service-forms';
import { AUTOCOMPLETE_DEBOUNCE_TIME } from '@fcom/service-forms/services/utils';
import {
  ButtonMode,
  ButtonSize,
  ButtonTheme,
  IconButtonTheme,
  IconButtonSize,
  IconPosition,
} from '@fcom/ui-components';
import { LanguageService } from '@fcom/ui-translate';
import { equals, isNotBlank, isObject, unsubscribe } from '@fcom/core/utils';
import { finShare } from '@fcom/rx';
import { LocationsService } from '@fcom/locations';

import { LocationSearchComponent } from '../location-search/location-search.component';
import { TimetableFeatureState } from '../../interfaces/index';
import { setSearch } from '../../store/actions/search.actions';
import { selectSearch } from '../..//store/selectors';

enum TimetableFormFields {
  ORIGIN = 'origin',
  DESTINATION = 'destination',
  DIRECT_FLIGHTS_ONLY = 'directFlights',
}

@Component({
  selector: 'fin-timetable-search',
  styleUrls: ['./timetable-search.component.scss'],
  templateUrl: './timetable-search.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TimetableSearchComponent implements OnInit, OnDestroy {
  @ViewChild('originLocationSearch') originLocationSearch: LocationSearchComponent;
  @ViewChild('destinationLocationSearch') destinationLocationSearch: LocationSearchComponent;

  public readonly ButtonMode = ButtonMode;
  public readonly ButtonSize = ButtonSize;
  public readonly ButtonTheme = ButtonTheme;
  public readonly IconPosition = IconPosition;
  public readonly IconButtonTheme = IconButtonTheme;
  public readonly IconButtonSize = IconButtonSize;
  public readonly SvgLibraryIcon = SvgLibraryIcon;

  private subscriptions: Subscription = new Subscription();

  reactiveForm: UntypedFormGroup;
  originLocations$: Observable<Location[]>;
  destinationLocations$: Observable<Location[]>;
  initialOrigin$: Observable<Location>;
  initialDestination$: Observable<Location>;

  locationSuggestions$: Observable<LocationSuggestions>;

  constructor(
    private fb: UntypedFormBuilder,
    private languageService: LanguageService,
    private locationRouteCffService: LocationRouteCffService,
    private store$: Store<TimetableFeatureState>,
    private locationService: LocationsService
  ) {}

  ngOnInit(): void {
    this.createForm();

    this.locationSuggestions$ = this.languageService.countryCode.pipe(
      switchMap((countryCode) =>
        this.locationRouteCffService.getSuggestedLocations(countryCode, this.languageService.localeValue)
      ),
      finShare()
    );

    this.onLocationChanges();

    this.initialOrigin$ = this.getInitialLocation('origin');
    this.initialDestination$ = this.getInitialLocation('destination');

    this.subscriptions.add(
      this.reactiveForm.statusChanges.pipe(filter((status) => status === 'VALID')).subscribe(() => {
        this.store$.dispatch(
          setSearch({
            origin: { code: this.origin.value.locationCode, title: this.origin.value.cityName },
            destination: { code: this.destination.value.locationCode, title: this.destination.value.cityName },
            directOnly: this.directFlights.value,
          })
        );
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions = unsubscribe(this.subscriptions);
  }

  getInitialLocation = (boundType: string): Observable<Location> => {
    return this.store$.pipe(
      selectSearch(),
      take(1),
      skipWhile((search) => !search[boundType]?.code),
      switchMap((search) => {
        this.locationService.triggerLocationFetch(search[boundType]?.code);

        return this.locationService.retrieveLocation(search[boundType]?.code).pipe(filter(Boolean));
      })
    );
  };

  switchLocations(): void {
    const newDestination = this.origin.value;
    this.originLocationSearch.setLocation(this.destination.value);
    this.destinationLocationSearch.setLocation(newDestination);
  }

  validateSelections(originChanged: boolean): void {
    if (
      this.reactiveForm.status === ReactiveFormStatus.INVALID &&
      this.reactiveForm.hasError('sameOriginAndDestination')
    ) {
      originChanged ? this.destination.setValue(null) : this.origin.setValue(null);
    }
  }

  private onLocationChanges(): void {
    this.originLocations$ = this.origin.valueChanges.pipe(
      startWith(''),
      debounceTime(AUTOCOMPLETE_DEBOUNCE_TIME),
      switchMap((val) => this.queryLocations(val, true)),
      share()
    );
    this.destinationLocations$ = this.destination.valueChanges.pipe(
      startWith(''),
      debounceTime(AUTOCOMPLETE_DEBOUNCE_TIME),
      switchMap((val) => this.queryLocations(val, false)),
      share()
    );
  }

  private createForm(): void {
    this.reactiveForm = this.fb.group(
      {
        [TimetableFormFields.ORIGIN]: ['', [locationFormatValidator(), Validators.required]],
        [TimetableFormFields.DESTINATION]: ['', [locationFormatValidator(), Validators.required]],
        [TimetableFormFields.DIRECT_FLIGHTS_ONLY]: [false],
      },
      {
        validator: Validators.compose([this.locationsNotSameValidator, Validators.required]),
      } as AbstractControlOptions
    );
  }

  get origin(): AbstractControl {
    return this.reactiveForm.get(TimetableFormFields.ORIGIN);
  }

  get destination(): AbstractControl {
    return this.reactiveForm.get(TimetableFormFields.DESTINATION);
  }

  get directFlights(): AbstractControl {
    return this.reactiveForm.get(TimetableFormFields.DIRECT_FLIGHTS_ONLY);
  }

  private queryLocations(queryString: string | { title: string }, forOrigin: boolean): Observable<Location[]> {
    const locale: string = this.languageService.localeValue;
    const searchText = isObject(queryString) ? queryString.title : queryString;
    return isNotBlank(searchText)
      ? this.locationRouteCffService.locationMatchesFor(searchText, locale).pipe(map((data) => data.items))
      : this.locationSuggestions$.pipe(
          map((locationSuggestions) =>
            forOrigin ? locationSuggestions?.departures : locationSuggestions?.destinations
          )
        );
  }

  private locationsNotSameValidator(control: AbstractControl): ValidationErrors | null {
    const currentOrigin = control.get(TimetableFormFields.ORIGIN).value;
    const currentDestination = control.get(TimetableFormFields.DESTINATION).value;
    if (
      typeof currentOrigin !== 'string' &&
      typeof currentDestination !== 'string' &&
      equals(currentOrigin, currentDestination)
    ) {
      return { sameOriginAndDestination: true };
    }

    return null;
  }
}
